/**
 * Post-load colour correction for Stonex X300 photographic colour.
 *
 * The X3A parser decodes the GRBG sensor into *raw* RGB and keeps the white
 * balance / exposure decision out of the pixels. Everything here is a per-frame
 * channel multiply, so switching modes is one O(n) pass over the colour array
 * with no reprojection and no re-parse.
 *
 * Green is the reference channel: the raw sensor is green-heavy, so red and
 * blue gains are normally >= 1 and green is left alone unless exposure moves it.
 */
/** Sentinel in the per-point frame index for points no camera frame coloured. */
export const STONEX_NO_FRAME = 0xffff;
/**
 * Show the capture as it was captured.
 *
 * Every correction here is off by default deliberately: the raw sample is the
 * only value in this pipeline that is a measurement rather than a decision, and
 * a viewer that silently rebalances it makes two archives incomparable for
 * reasons the user never chose. `band` was the previous default and remains one
 * selection away in the file's Camera colour controls, along with per-frame
 * gray-world and exposure matching — which is where cross-camera brightness
 * differences are meant to be addressed, when the user asks for it.
 */
export const DEFAULT_STONEX_COLOR_CORRECTION = {
    whiteBalance: 'off',
    exposure: 'off',
    highlight: 'clip',
    manualRedGain: 1,
    manualBlueGain: 1,
    exposureStops: 0,
};
export function normalizeStonexColorCorrection(value) {
    return { ...DEFAULT_STONEX_COLOR_CORRECTION, ...(value ?? {}) };
}
function median(values) {
    if (values.length === 0) {
        return 0;
    }
    const sorted = [...values].sort((a, b) => a - b);
    const middle = sorted.length >> 1;
    return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) * 0.5 : sorted[middle];
}
export function buildStonexColorCalibration(frames, bandGains) {
    return {
        frames,
        bandGains,
        targetGreen: median(frames.map(frame => frame.meanGreen).filter(value => value > 0)),
    };
}
/**
 * Per-frame [red, green, blue] multipliers for the given settings.
 * Length is `calibration.frames.length * 3`.
 */
export function computeStonexFrameMultipliers(calibration, settings) {
    const multipliers = new Float32Array(calibration.frames.length * 3);
    const exposureScale = settings.exposure === 'manual' ? Math.pow(2, settings.exposureStops) : 1;
    for (let index = 0; index < calibration.frames.length; index++) {
        const frame = calibration.frames[index];
        let redGain = 1;
        let blueGain = 1;
        switch (settings.whiteBalance) {
            case 'per-frame':
                redGain = frame.grayRedGain;
                blueGain = frame.grayBlueGain;
                break;
            case 'band': {
                const band = calibration.bandGains[frame.type];
                redGain = band?.redGain ?? frame.grayRedGain;
                blueGain = band?.blueGain ?? frame.grayBlueGain;
                break;
            }
            case 'manual':
                redGain = settings.manualRedGain;
                blueGain = settings.manualBlueGain;
                break;
            case 'off':
                break;
        }
        // Exposure lifts all three channels together so it cannot shift hue.
        let exposure = exposureScale;
        if (settings.exposure === 'match' && frame.meanGreen > 0 && calibration.targetGreen > 0) {
            exposure = calibration.targetGreen / frame.meanGreen;
        }
        const offset = index * 3;
        multipliers[offset] = redGain * exposure;
        multipliers[offset + 1] = exposure;
        multipliers[offset + 2] = blueGain * exposure;
    }
    return multipliers;
}
function writeCorrected(out, offset, red, green, blue, preserveHue) {
    if (preserveHue) {
        // Scaling all three by the same factor keeps the hue of blown highlights,
        // instead of clipping red and blue first and leaving a green cast.
        const peak = Math.max(red, green, blue);
        if (peak > 255) {
            const scale = 255 / peak;
            red *= scale;
            green *= scale;
            blue *= scale;
        }
    }
    out[offset] = red < 0 ? 0 : red > 255 ? 255 : Math.round(red);
    out[offset + 1] = green < 0 ? 0 : green > 255 ? 255 : Math.round(green);
    out[offset + 2] = blue < 0 ? 0 : blue > 255 ? 255 : Math.round(blue);
}
/**
 * Apply per-frame multipliers to a raw point-colour array.
 * `out` may alias nothing else; passing the live `colorsArray` mutates the
 * buffer the GPU attribute already shares, so only `needsUpdate` is required.
 */
export function applyStonexColorCorrectionToPoints(raw, frameIndex, multipliers, settings, out) {
    const preserveHue = settings.highlight === 'preserve-hue';
    const pointCount = frameIndex.length;
    const frameCount = multipliers.length / 3;
    // The highlight mode is the same for every point, so it picks the loop rather
    // than being tested inside one.
    //
    // This stays JavaScript, and unusually the measurement says it must: the Rust
    // version was written and A/B'd in the same page at 20M points and came out
    // *slower*, 220ms against 151ms. The reason is `out` — this writes straight
    // into the live colour attribute, while the port had to copy the raw colours
    // and frame indices in, allocate the result in wasm, copy it back out and
    // then copy it into `out`. Roughly 280MB of traffic to replace a loop that
    // does none. It only becomes worth porting if the raw colours stop crossing
    // the boundary at all, which means the colour session keeping them - see
    // "colour never leaves Rust" in the backlog.
    if (preserveHue) {
        for (let point = 0; point < pointCount; point++) {
            const offset = point * 3;
            const frame = frameIndex[point];
            if (frame >= frameCount) {
                out[offset] = raw[offset];
                out[offset + 1] = raw[offset + 1];
                out[offset + 2] = raw[offset + 2];
                continue;
            }
            const gain = frame * 3;
            let red = raw[offset] * multipliers[gain];
            let green = raw[offset + 1] * multipliers[gain + 1];
            let blue = raw[offset + 2] * multipliers[gain + 2];
            // Scaling all three by the same factor keeps the hue of blown highlights,
            // instead of clipping red and blue first and leaving a green cast.
            const peak = red > green ? (red > blue ? red : blue) : green > blue ? green : blue;
            if (peak > 255) {
                const scale = 255 / peak;
                red *= scale;
                green *= scale;
                blue *= scale;
            }
            out[offset] = red < 0 ? 0 : red > 255 ? 255 : Math.round(red);
            out[offset + 1] = green < 0 ? 0 : green > 255 ? 255 : Math.round(green);
            out[offset + 2] = blue < 0 ? 0 : blue > 255 ? 255 : Math.round(blue);
        }
        return;
    }
    for (let point = 0; point < pointCount; point++) {
        const offset = point * 3;
        const frame = frameIndex[point];
        if (frame >= frameCount) {
            // Uncoloured points keep the parser's neutral fill.
            out[offset] = raw[offset];
            out[offset + 1] = raw[offset + 1];
            out[offset + 2] = raw[offset + 2];
            continue;
        }
        const gain = frame * 3;
        const red = raw[offset] * multipliers[gain];
        const green = raw[offset + 1] * multipliers[gain + 1];
        const blue = raw[offset + 2] * multipliers[gain + 2];
        out[offset] = red < 0 ? 0 : red > 255 ? 255 : Math.round(red);
        out[offset + 1] = green < 0 ? 0 : green > 255 ? 255 : Math.round(green);
        out[offset + 2] = blue < 0 ? 0 : blue > 255 ? 255 : Math.round(blue);
    }
}
/** Apply one frame's multipliers to that frame's raw RGBA preview thumbnail. */
export function applyStonexColorCorrectionToPreview(rawRgba, multipliers, frameIndex, settings, out) {
    const preserveHue = settings.highlight === 'preserve-hue';
    const gain = frameIndex * 3;
    const redMultiplier = multipliers[gain] ?? 1;
    const greenMultiplier = multipliers[gain + 1] ?? 1;
    const blueMultiplier = multipliers[gain + 2] ?? 1;
    for (let offset = 0; offset + 3 < rawRgba.length; offset += 4) {
        writeCorrected(out, offset, rawRgba[offset] * redMultiplier, rawRgba[offset + 1] * greenMultiplier, rawRgba[offset + 2] * blueMultiplier, preserveHue);
        out[offset + 3] = rawRgba[offset + 3];
    }
}
